import axios from 'axios';
import cn from 'classnames';
import { FormikErrors, useFormik } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { CustomSelect } from '../../../../UI/CustomSelect/CustomSelect';
import { DatePicker } from '../../../../UI/DatePicker/DatePicker';
import { Input } from '../../../../UI/Input/Input';
import { RichTextEditor } from '../../../../UI/RichTextEditor/RichTextEditor';
import { AddEventImage } from '../../../../components/AddEventImage/AddEventImage';
import { AddNewsHeader } from '../../../../components/AddNewsHeader/AddNewsHeader';
import { axiosInstance } from '../../../../helpers/axios';
import { useFetchRubrics } from '../../../../hooks/useFetchRubrics';
import { PATH_DASHBOARD } from '../../../../routes/RouterConfig';
import { IArticle, SelectOption } from '../../../../types/types';
import styles from './ArticleForm.module.scss';

// тот же лимит на обложку, что у новостей и мероприятий
const MAX_IMAGE_SIZE = 512000;

// подсказка под полем: всплывающая подсказка у «!» рассчитана на одну строку
const TOP_HELP =
    'Материал встанет в блок «Полезное» на главной. Он там один: ' +
    'у прежнего материала галочка снимется.';

interface ArticleValues {
    title: string;
    rubric: string;
    publish_date: string;
    is_top: boolean;
    text: string;
}

interface ArticleFormProps {
    // без материала форма создаёт новый
    article?: IArticle;
}

export const ArticleForm = ({ article }: ArticleFormProps) => {
    const navigate = useNavigate();
    const [selectedImages, setSelectedImages] = useState<FileList | null>(null);
    const { rubrics } = useFetchRubrics();
    const rubricOptions: SelectOption[] = rubrics.map((item) => ({
        value: String(item.id),
        label: item.title,
    }));
    const [isLoading, setIsLoading] = useState(false);

    const isValidImage =
        !selectedImages?.length || selectedImages[0].size < MAX_IMAGE_SIZE;
    const hasImage = Boolean(article?.image || selectedImages?.length);

    const formik = useFormik<ArticleValues>({
        initialValues: {
            title: article?.title ?? '',
            rubric: article ? String(article.rubric.id) : '',
            publish_date: article?.publish_date ?? '',
            is_top: article?.is_top ?? false,
            text: article?.text ?? '',
        },
        validateOnMount: Boolean(article),
        validationSchema: yup.object().shape({
            title: yup
                .string()
                .required('Поле обязательно')
                .max(256, 'Заголовок не должен содержать более 256 символов'),
            rubric: yup.string().required('Выберите рубрику'),
            publish_date: yup.string().required('Поле обязательно'),
            text: yup.string().required('Поле обязательно'),
        }),
        onSubmit: async (values) => {
            if (!hasImage) {
                toast.error('Необходимо добавить обложку');
                return;
            }
            const formData = new FormData();
            (Object.keys(values) as Array<keyof ArticleValues>).forEach((key) =>
                formData.append(key, String(values[key])),
            );
            if (selectedImages?.length) {
                formData.append('image', selectedImages[0]);
            }

            setIsLoading(true);
            try {
                if (article) {
                    await axiosInstance.put(
                        `/v1/article/${article.id}/`,
                        formData,
                    );
                } else {
                    await axiosInstance.post('/v1/article/', formData);
                }
                toast.success(
                    article ? 'Материал обновлён' : 'Материал опубликован',
                );
                navigate(PATH_DASHBOARD.staff.useful.root);
            } catch (error) {
                // ошибки по полям с бэкенда, например ссылка не на VK Видео или RuTube
                if (
                    axios.isAxiosError(error) &&
                    error.response?.status === 400
                ) {
                    const errors = Object.fromEntries(
                        Object.entries(error.response.data).map(
                            ([field, messages]) => [
                                field,
                                ([] as unknown[]).concat(messages).join(' '),
                            ],
                        ),
                    );
                    formik.setErrors(errors as FormikErrors<ArticleValues>);
                }
                toast.error('Не удалось сохранить материал');
            } finally {
                setIsLoading(false);
            }
        },
    });

    return (
        // отправляет кнопка в шапке, а не форма: иначе запрос уходил бы дважды
        <form
            className={styles.form}
            onSubmit={(event) => event.preventDefault()}
        >
            <AddNewsHeader
                dirty={formik.dirty || Boolean(selectedImages?.length)}
                isValid={formik.isValid && isValidImage}
                isLoading={isLoading}
                onSubmit={formik.handleSubmit}
                resetForm={() => navigate(-1)}
                title={article ? 'Изменить материал' : 'Добавить материал'}
                extraCondition={hasImage}
            />
            <AddEventImage
                selectedImages={selectedImages}
                setSelectedImages={setSelectedImages}
                showImage={article?.image ?? null}
            />
            {!isValidImage ? (
                <span className={styles.validImage}>
                    Максимальный размер изображения 500Кб
                </span>
            ) : null}
            <div className={styles.form__content}>
                <Input
                    wrapperClassName={[styles.form_title]}
                    placeholder="Материал"
                    fieldClassName={styles.input__field}
                    inputHeader="Название материала"
                    headerClassName={styles.input__title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.title}
                    name="title"
                    errorMessage={
                        formik.touched.title ? formik.errors.title : undefined
                    }
                    isValid={!formik.errors.title && !!formik.values.title}
                />
                <CustomSelect
                    className={styles.form_rubric}
                    titleClass={styles.input__title}
                    title="Рубрика"
                    borderRadius="17px"
                    placeholderSize="15px"
                    placeholder="Выберите рубрику"
                    name="rubric"
                    options={rubricOptions}
                    value={
                        rubricOptions.find(
                            (option) => option.value === formik.values.rubric,
                        ) ?? null
                    }
                    onChange={(option: SelectOption) =>
                        formik.setFieldValue('rubric', option.value)
                    }
                    errorMessage={
                        formik.touched.rubric ? formik.errors.rubric : undefined
                    }
                    isSearchable={false}
                />
                <DatePicker
                    wrapperClassName={[styles.form_date]}
                    placeholder="02.03.2023"
                    headerText="Дата"
                    headerClassName={styles.input__title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.publish_date}
                    name="publish_date"
                    errorMessage={
                        formik.touched.publish_date
                            ? formik.errors.publish_date
                            : undefined
                    }
                    isValid={
                        !formik.errors.publish_date &&
                        !!formik.values.publish_date
                    }
                />
                <div className={styles.form_top}>
                    <label className={styles.topToggle}>
                        <input
                            type="checkbox"
                            name="is_top"
                            checked={formik.values.is_top}
                            onChange={formik.handleChange}
                        />
                        Показывать на главной
                    </label>
                    <p className={styles.hint}>{TOP_HELP}</p>
                </div>
                <RichTextEditor
                    wrapperClassName={styles.form_text}
                    headerClassName={styles.input__title}
                    title="Текст"
                    value={formik.values.text}
                    onChange={(html) => formik.setFieldValue('text', html)}
                    onBlur={() => formik.setFieldTouched('text', true)}
                    errorMessage={
                        formik.touched.text ? formik.errors.text : undefined
                    }
                />
            </div>
        </form>
    );
};
